import type { Screenshot } from 'types';
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, ImageIcon } from 'lucide-react';
import { cn } from 'helpers/cn';
import { useScreenshotImage } from '@/state/experiments/useScreenshotImage';

type ScreenshotsCellProps = {
  controlScreenshots: Screenshot[];
  variantScreenshots: Screenshot[];
  /** Set on the first row only, for the product tour. Not set when there is nothing to show. */
  tourAnchor?: string;
};

export const ScreenshotsCell = ({ controlScreenshots, variantScreenshots, tourAnchor }: ScreenshotsCellProps) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'before' | 'after'>('before');
  const [activeIndex, setActiveIndex] = useState(0);

  const gallery = activeTab === 'before' ? controlScreenshots : variantScreenshots;
  const current = gallery[activeIndex];

  const openPreview = (tab: 'before' | 'after') => {
    setActiveTab(tab);
    setActiveIndex(0);
    setOpen(true);
  };

  const prev = () => {
    if (!gallery.length) return;
    setActiveIndex((i) => (i - 1 + gallery.length) % gallery.length);
  };

  const next = () => {
    if (!gallery.length) return;
    setActiveIndex((i) => (i + 1) % gallery.length);
  };

  const hasBefore = controlScreenshots.length > 0;
  const hasAfter = variantScreenshots.length > 0;

  if (!hasBefore && !hasAfter) {
    return <span className="supporting">-</span>;
  }

  return (
    <>
      <div className="flex items-center gap-2" data-tour={tourAnchor}>
        {hasBefore && <DashedBox label="Before" onClick={() => openPreview('before')} />}
        {hasAfter && <DashedBox label="After" onClick={() => openPreview('after')} />}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-panel border-line rounded-panel overflow-hidden border p-0 sm:max-w-3xl [&_[aria-label=Close]]:hidden">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle className="micro-label text-foreground flex items-center gap-2">
              <ImageIcon className="size-3.5" strokeWidth={1.5} />
              {activeTab === 'before' ? 'Before' : 'After'} ({gallery.length})
            </DialogTitle>
          </DialogHeader>

          <div className="bg-background border-line rounded-card relative mx-4 mb-2 flex aspect-video items-center justify-center overflow-hidden border">
            {current ? (
              <ScreenshotImage screenshot={current} index={activeIndex} />
            ) : (
              <div className="supporting">No images</div>
            )}

            {gallery.length > 1 && (
              <>
                <button
                  onClick={prev}
                  aria-label="Previous screenshot"
                  className="bg-panel border-line text-dim hover:text-foreground rounded-pill absolute left-3 top-1/2 -translate-y-1/2 border p-2 transition-colors duration-150"
                >
                  <ChevronLeft className="size-4" strokeWidth={1.5} />
                </button>
                <button
                  onClick={next}
                  aria-label="Next screenshot"
                  className="bg-panel border-line text-dim hover:text-foreground rounded-pill absolute right-3 top-1/2 -translate-y-1/2 border p-2 transition-colors duration-150"
                >
                  <ChevronRight className="size-4" strokeWidth={1.5} />
                </button>
              </>
            )}
          </div>

          <div className="border-line flex items-center justify-between border-t px-4 py-3">
            <div className="flex gap-2">
              <GalleryTab
                label={`Before (${controlScreenshots.length})`}
                isActive={activeTab === 'before'}
                disabled={!hasBefore}
                onClick={() => {
                  setActiveTab('before');
                  setActiveIndex(0);
                }}
              />
              <GalleryTab
                label={`After (${variantScreenshots.length})`}
                isActive={activeTab === 'after'}
                disabled={!hasAfter}
                onClick={() => {
                  setActiveTab('after');
                  setActiveIndex(0);
                }}
              />
            </div>
            <DialogClose asChild>
              <button
                type="button"
                className="border-line text-dim hover:text-foreground hover:bg-hover flex h-7 items-center rounded-lg border bg-transparent px-3 text-meta transition-colors duration-150"
              >
                Close
              </button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

type GalleryTabProps = { label: string; isActive: boolean; disabled: boolean; onClick: () => void };

const GalleryTab = ({ label, isActive, disabled, onClick }: GalleryTabProps) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'micro-label border-line rounded-pill border px-[11px] py-[6px] transition-colors duration-150 disabled:opacity-40',
      isActive && 'bg-sel-bg border-sel-line text-foreground',
    )}
  >
    {label}
  </button>
);

/**
 * The thumbnail stand-in. It carries a `field` fill rather than sitting on the row's
 * own surface: a 10%-alpha dashed hairline is not enough to draw a 40x48 target on its
 * own, and the fill is what makes the box read as something to click.
 */
const DashedBox = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button
    onClick={(ev) => {
      ev.stopPropagation();
      onClick();
    }}
    className="border-line bg-field hover:border-sel-line rounded-card grid h-10 w-12 place-items-center border border-dashed transition-colors duration-150"
  >
    <span className="micro-label">{label}</span>
  </button>
);

/**
 * Screenshot urls are stored as bare paths (/screenshots/<company>/<website>/...)
 * and are served by the files API behind auth, so they cannot be used as an
 * <img src> directly, that resolves against the dashboard origin and 404s.
 * useScreenshotImage fetches the blob through the authenticated files client and
 * hands back an object URL, which is what the experiment info gallery does too.
 */
const ScreenshotImage = ({ screenshot, index }: { screenshot: Screenshot; index: number }) => {
  const { imageSrc, isLoading } = useScreenshotImage(screenshot.url);

  if (isLoading) return <div className="supporting">Loading…</div>;
  if (!imageSrc) return <div className="supporting">Image unavailable</div>;

  return (
    <img
      src={imageSrc}
      alt={screenshot.comment || `Screenshot ${index + 1}`}
      className="h-full w-full object-contain"
    />
  );
};
